import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  CircularProgress,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from '@mui/material';
import { CloudUpload, Delete, Visibility } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { Icon } from '../../types';
import type { IconChannel } from '../User/Icons';

interface AdminIconsProps {
  userId: string;
  channel?: IconChannel;
}

const toCdnUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'firebasestorage.googleapis.com') {
      return url;
    }
    const segments = parsed.pathname.split('/').filter(Boolean);
    const bucketIndex = segments.indexOf('b');
    const objectIndex = segments.indexOf('o');
    if (bucketIndex !== -1 && objectIndex !== -1 && objectIndex + 1 < segments.length) {
      const bucket = segments[bucketIndex + 1];
      const objectPath = segments.slice(objectIndex + 1).join('/');
      parsed.hostname = 'mphoto.mphotovn.online';
      parsed.pathname = `/${bucket}/${objectPath}`;
      return parsed.toString();
    }
    return url;
  } catch {
    return url;
  }
};

const AdminIcons: React.FC<AdminIconsProps> = ({ userId, channel = 'lite' }) => {
  const isPro = channel === 'pro';
  const [icons, setIcons] = useState<Icon[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedIconId, setSelectedIconId] = useState<string | null>(null);

  const fetchIcons = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = isPro ? await api.getUserProIcons(userId) : await api.getUserIcons(userId);
      setIcons(data);
    } catch (error) {
      console.error('Error fetching icons:', error);
      toast.error('Failed to load icons');
    } finally {
      setIsLoading(false);
    }
  }, [isPro, userId]);

  useEffect(() => {
    fetchIcons();
  }, [fetchIcons]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
    },
    maxFiles: 30,
    onDrop: async (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      const invalidTypeFile = acceptedFiles.find((file) => file.type !== 'image/png');
      if (invalidTypeFile) {
        toast.error(`File ${invalidTypeFile.name} không phải PNG`);
        return;
      }

      try {
        setIsUploading(true);
        const formData = new FormData();
        acceptedFiles.forEach((file) => {
          formData.append('icons', file);
        });

        if (isPro) {
          await api.uploadUserProIcons(userId, formData);
        } else {
          await api.uploadUserIcons(userId, formData);
        }
        toast.success(`Upload ${acceptedFiles.length} icon thành công`);
        fetchIcons();
      } catch (error: any) {
        const message = error?.response?.data?.error || 'Failed to upload icons';
        toast.error(message);
        console.error('Upload error:', error);
      } finally {
        setIsUploading(false);
      }
    },
  });

  const handleDeleteClick = (iconId: string) => {
    setSelectedIconId(iconId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedIconId) return;

    try {
      if (isPro) {
        await api.adminDeleteUserProIcon(userId, selectedIconId);
      } else {
        await api.adminDeleteUserIcon(userId, selectedIconId);
      }
      setIcons((prev) => prev.filter((icon) => icon._id !== selectedIconId));
      toast.success('Icon deleted successfully');
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Failed to delete icon';
      toast.error(message);
      console.error('Delete error:', error);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedIconId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedIconId(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          mb: 4,
          textAlign: 'center',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {isDragActive
            ? 'Drop PNG icon files here'
            : 'Drag and drop PNG icon files here, or click to select'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isPro ? 'Pro — ' : 'Lite — '}
          Chỉ nhận PNG, tối đa 30 file/lần. File trên 500KB sẽ tự nén khi upload.
        </Typography>
        {isUploading && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Paper>

      {icons.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={6}
          sx={{ backgroundColor: 'background.paper', borderRadius: 1 }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Icons Found
          </Typography>
          <Typography color="text.secondary">This user hasn&apos;t uploaded any icons yet</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
          }}
        >
          {icons.map((icon) => (
            <Card key={icon._id}>
              <CardMedia
                component="img"
                height="200"
                image={toCdnUrl(icon.url)}
                alt={icon.name}
                sx={{
                  objectFit: 'contain',
                  backgroundColor: '#ffffff',
                  p: 2,
                }}
              />
              <CardActions>
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  href={toCdnUrl(icon.url)}
                  target="_blank"
                >
                  View
                </Button>
                <IconButton onClick={() => handleDeleteClick(icon._id)} color="error">
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Icon</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this icon? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminIcons;
